import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { hrApi } from '../../services/hrApi.js';
import { formatNumber } from '../../utils/formatters.js';

/**
 * Der Jahresabschluss für die Ferien.
 *
 * Ohne ihn verschwanden Restferien am 1. Januar: es gab keinen Vorgang, der
 * sie ins neue Jahr trägt, und keine Spalte, in der ein Übertrag hätte
 * stehen können.
 *
 * Erst sehen, dann tun. Der Abschluss verschiebt Guthaben und lässt Tage
 * verfallen - wer ihn auslöst, soll vorher wissen, bei wem wie viel. Die
 * Vorschau ist deshalb kein Zusatz, sondern der erste Schritt.
 */
export function YearCloseModal({ open, onClose, year, onDone }) {
  const { t } = useTranslation();
  /* Leer heisst "alles übertragen", "0" heisst "nichts". Das ist ein
     Unterschied, den eine Zahl allein nicht ausdrücken kann - deshalb ein
     Textfeld und keine Zahl mit Vorgabewert. */
  const [maxCarry, setMaxCarry] = useState('');
  const [expiresOn, setExpiresOn] = useState(`${year + 1}-03-31`);
  const [ergebnis, setErgebnis] = useState(null);
  const [fehler, setFehler] = useState(null);

  const params = {
    year,
    leaveTypeCode: 'ANNUAL',
    ...(maxCarry.trim() === '' ? {} : { maxCarry: Number(maxCarry) }),
    ...(expiresOn ? { expiresOn } : {}),
  };

  const vorschau = useQuery({
    queryKey: ['hr', 'yearClose', params],
    queryFn: () => hrApi.yearClosePreview(params).then((r) => r.items),
    enabled: open && !ergebnis,
    retry: false,
  });

  const durchführen = useMutation({
    mutationFn: () => hrApi.yearClose(params),
    onSuccess: (data) => {
      setFehler(null);
      setErgebnis(data);
      onDone?.();
    },
    onError: (err) => setFehler(err?.message || t('common.loadError')),
  });

  const zeilen = vorschau.data?.rows || [];
  const summen = vorschau.data?.totals;

  const spalten = [
    { key: 'who', label: t('hr.leave.col.employee'), render: (r) => r.employeeName },
    {
      key: 'remaining',
      label: t('hr.leave.close.col.remaining'),
      align: 'right',
      render: (r) => <span className="num mono">{formatNumber(r.remaining, 1)}</span>,
    },
    {
      key: 'carry',
      label: t('hr.leave.close.col.carry'),
      align: 'right',
      render: (r) => <strong className="num mono">{formatNumber(r.carry, 1)}</strong>,
    },
    {
      key: 'forfeited',
      label: t('hr.leave.close.col.forfeited'),
      align: 'right',
      /* Was verfällt, steht in der Vorschau - nicht erst im Rückblick.
         Das ist die Zahl, wegen der jemand den Deckel noch einmal
         überdenkt. */
      render: (r) => (
        <span className="num mono" style={r.forfeited > 0 ? { color: 'var(--color-danger)' } : undefined}>
          {formatNumber(r.forfeited, 1)}
        </span>
      ),
    },
  ];

  function schliessen() {
    setErgebnis(null);
    setFehler(null);
    onClose();
  }

  return (
    <Modal
      open={open}
      title={`${t('hr.leave.close.title')} ${year}`}
      onClose={schliessen}
      footer={
        <>
          <Button variant="ghost" onClick={schliessen}>
            {ergebnis ? t('common.close') : t('common.cancel')}
          </Button>
          {!ergebnis && (
            <Button
              variant="primary"
              onClick={() => durchführen.mutate()}
              disabled={durchführen.isPending || zeilen.length === 0}
            >
              {t('hr.leave.close.run')}
            </Button>
          )}
        </>
      }
    >
      <p className="muted text-sm">{t('hr.leave.close.subtitle')}</p>

      {ergebnis ? (
        <>
          <div className="alert success">
            {t('hr.leave.close.done', { count: ergebnis.closed, carried: formatNumber(ergebnis.carried, 1) })}
          </div>
          {ergebnis.skipped?.length > 0 && (
            <p className="muted text-sm">
              {t('hr.leave.close.skipped', { names: ergebnis.skipped.join(', ') })}
            </p>
          )}
        </>
      ) : (
        <>
          <div className="field-row">
            <label className="field">
              <span className="field-label">{t('hr.leave.close.maxCarry')}</span>
              <input
                className="input"
                type="number"
                min="0"
                step="0.5"
                value={maxCarry}
                onChange={(e) => setMaxCarry(e.target.value)}
                placeholder="-"
              />
              <span className="muted text-xs">{t('hr.leave.close.maxCarryHint')}</span>
            </label>
            <label className="field">
              <span className="field-label">{t('hr.leave.close.expires')}</span>
              <input
                className="input"
                type="date"
                value={expiresOn}
                onChange={(e) => setExpiresOn(e.target.value)}
              />
            </label>
          </div>

          {summen && (
            <p className="text-sm">
              {t('hr.leave.close.col.carry')}: <strong className="num mono">{formatNumber(summen.carry, 1)}</strong>
              {' · '}
              {t('hr.leave.close.col.forfeited')}:{' '}
              <strong className="num mono">{formatNumber(summen.forfeited, 1)}</strong>
            </p>
          )}

          <Table
            columns={spalten}
            rows={zeilen}
            isLoading={vorschau.isLoading}
            emptyMessage={t('hr.leave.balance.empty')}
            getRowKey={(r) => r.employeeId}
          />
        </>
      )}

      {fehler && <div className="alert danger">{fehler}</div>}
    </Modal>
  );
}
